import { Box, List, Typography, Button, CircularProgress, Alert } from '@mui/material';
import { RequestListItem } from './RequestListItem';
import type { RequestListItemVariant } from './RequestListItem';
import { RequestListControls } from './RequestListControls';
import type { RequestListControlsConfig } from './RequestListControls';
import type { FHRequest } from '../types';

interface RequestListProps {
  items: FHRequest[];
  loading: boolean;
  emptyMessage?: string;
  emptyAction?: React.ReactNode;
  hasMore?: boolean;
  onLoadMore?: () => void;
  variant: RequestListItemVariant;
  onItemClaim?: (requestKey: string) => void;
  onItemHide?: (requestKey: string, reason: number) => void;
  onItemClick?: (requestKey: string) => void;
  error?: string | null;
  controls?: RequestListControlsConfig;
  clickBehavior?: 'navigate' | 'expand';
  currentUserUuid?: string;
}

export const RequestList = ({
  items,
  loading,
  emptyMessage = 'No items found.',
  emptyAction,
  hasMore = false,
  onLoadMore,
  variant,
  onItemClaim,
  onItemHide,
  onItemClick,
  error,
  controls,
  clickBehavior,
  currentUserUuid,
}: RequestListProps) => {
  // Loading state (initial load)
  if (loading && items.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  // Empty state
  if (items.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="text.secondary">
          {emptyMessage}
        </Typography>
        {emptyAction}
      </Box>
    );
  }

  return (
    <>
      {/* Optional controls */}
      {controls && (controls.filterEnabled || controls.sortEnabled) && (
        <RequestListControls
          filterOptions={controls.filterEnabled ? controls.filterOptions : undefined}
          sortOptions={controls.sortEnabled ? controls.sortOptions : undefined}
          currentFilter={controls.currentFilter}
          currentSort={controls.currentSort}
          onFilterChange={controls.onFilterChange}
          onSortChange={controls.onSortChange}
          filterLabel={controls.filterLabel}
          sortLabel={controls.sortLabel}
        />
      )}

      {/* List items */}
      <List>
        {items.map((request, index) => (
          <RequestListItem
            key={request.key}
            request={request}
            variant={variant}
            showDivider={index > 0}
            onClaim={onItemClaim}
            onHide={onItemHide}
            onClick={onItemClick}
            clickBehavior={clickBehavior}
            currentUserUuid={currentUserUuid}
          />
        ))}
      </List>

      {/* Pagination */}
      {hasMore && onLoadMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <Button
            variant="text"
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            Load More
          </Button>
        </Box>
      )}
    </>
  );
};
